import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from '@/lib/toast'
import { commonQueryKeys } from '@/hooks/useCommon'
import type {
  ContentGenerationsResponse,
  GenerateContentBody,
  GenerateContentResponse,
  GenerateContentPendingResponse,
  ContentGenerationStatusResponse,
} from '@/types'

// Helper function to extract error message from API errors
const extractErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error
  }
  
  if (error?.response?.data) {
    const data = error.response.data
    
    // Handle FastAPI validation errors (array of error objects)
    if (Array.isArray(data)) {
      return data.map((err: any) => {
        if (typeof err === 'string') return err
        if (err?.msg) return err.msg
        if (err?.message) return err.message
        return JSON.stringify(err)
      }).join(', ')
    }
    
    // Handle object with detail field
    if (typeof data === 'object' && data.detail) {
      if (typeof data.detail === 'string') {
        return data.detail
      }
      if (Array.isArray(data.detail)) {
        return data.detail.map((err: any) => {
          if (typeof err === 'string') return err
          if (err?.msg) return err.msg
          if (err?.message) return err.message
          return JSON.stringify(err)
        }).join(', ')
      }
    }
    
    // Handle direct message field
    if (data.message && typeof data.message === 'string') {
      return data.message
    }
  }
  
  if (error?.message && typeof error.message === 'string') {
    return error.message
  }
  
  // Fallback: stringify the error
  return JSON.stringify(error) || 'An unexpected error occurred'
}

// Query keys
export const contentQueryKeys = {
  all: ['content'] as const,
  generations: () => [...contentQueryKeys.all, 'generations'] as const,
  history: () => [...contentQueryKeys.all, 'history'] as const,
  historyByProfile: (profileId: string, page?: number, limit?: number, isArchived?: boolean) => 
    [...contentQueryKeys.history(), profileId, page, limit, isArchived] as const,
} as const

// Content generations query
export const useContentGenerations = (options = {}) => {
  return useQuery({
    queryKey: contentQueryKeys.generations(),
    queryFn: async () => {
      const response = await api.get<ContentGenerationsResponse>('/content-generations')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Content history query
export const useContentHistory = (options = {}) => {
  return useQuery({
    queryKey: contentQueryKeys.history(),
    queryFn: async () => {
      const response = await api.get<ContentGenerationsResponse>('/content-generations/history')
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

// Content history by profile query with pagination and archive filtering
export const useContentHistoryByProfile = (
  profileId: string, 
  options: {
    page?: number
    limit?: number
    isArchived?: boolean
    [key: string]: any
  } = {}
) => {
  const { page = 1, limit = 10, isArchived = false, ...queryOptions } = options
  
  return useQuery({
    queryKey: contentQueryKeys.historyByProfile(profileId, page, limit, isArchived),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', limit.toString())
      if (isArchived !== undefined) {
        params.append('is_archived', isArchived.toString())
      }
      
      const response = await api.get<ContentGenerationsResponse>(
        `/content/generations/history/${profileId}?${params.toString()}`
      )
      
      // Extract pagination metadata from headers
      // Axios lowercases header names, so we use lowercase
      // Also check for case variations as a fallback
      const getHeader = (name: string) => {
        return response.headers[name.toLowerCase()] || 
               response.headers[name] || 
               (response.headers as any)[name.toLowerCase()] ||
               (response.headers as any)[name] ||
               '0'
      }
      
      const totalCount = parseInt(getHeader('x-total-count'), 10)
      const currentPage = parseInt(getHeader('x-page'), 10)
      const perPage = parseInt(getHeader('x-per-page'), 10)
      const totalPages = parseInt(getHeader('x-total-pages'), 10)
      
      // Debug logging to help diagnose pagination issues
      // console.log('📄 [useContentHistoryByProfile] Pagination headers:', {
      //   'x-total-count': getHeader('x-total-count'),
      //   'x-page': getHeader('x-page'),
      //   'x-per-page': getHeader('x-per-page'),
      //   'x-total-pages': getHeader('x-total-pages'),
      //   parsed: { totalCount, currentPage, perPage, totalPages },
      //   allHeaders: Object.keys(response.headers)
      // })
      
      return {
        data: response.data,
        pagination: {
          totalCount,
          currentPage,
          perPage,
          totalPages,
        }
      }
    },
    enabled: Number(profileId) > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...queryOptions,
  })
}

// Generate content mutation
export const useGenerateContent = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: GenerateContentBody): Promise<GenerateContentPendingResponse> => {
      // Backend now returns 202 immediately with { generation_id, status: "pending" }
      // Frontend polls GET /content/generations/{generation_id} for the result
      const response = await api.post<GenerateContentPendingResponse>('/content/generate', data, {
        timeout: 15000, // 15 seconds - just enough for the server to accept and queue the job
      })
      return response.data
    },
    // onSuccess intentionally omitted: the mutation only kicks off the async job.
    // Query invalidation and toast are handled in ContentGenerator after polling confirms success.
    // Error handling removed - let component handle errors with Alert for better visibility
    // This prevents duplicate error messages (toast + Alert)
  })
}

// Poll generation status until success or failed
export const useGenerationStatus = (generationId: number | null) => {
  return useQuery({
    queryKey: ['generation-status', generationId],
    queryFn: async (): Promise<ContentGenerationStatusResponse> => {
      const response = await api.get<ContentGenerationStatusResponse>(
        `/content/generations/${generationId}`
      )
      return response.data
    },
    enabled: generationId !== null && generationId > 0,
    retry: 3,
    staleTime: Infinity,
    refetchInterval: (query) => {
      const d = query.state.data
      return (!d || d.status === 'pending') ? 3000 : false
    },
  })
}

// Note: Multi-platform content generation endpoint not available in current API
// export const useGenerateMultiPlatformContent = () => { ... }

// Archive content generation mutation (renamed from delete)
export const useArchiveContentGeneration = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/content/generations/${id}/archive`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.generations() })
      queryClient.invalidateQueries({ queryKey: contentQueryKeys.history() })
      toast.success('content.archivedSuccess')
    },
    onError: (error: any) => {
      const errorMessage = extractErrorMessage(error) || 'Failed to archive content'
      toast.raw.error(errorMessage)
    },
  })
}

// Prefill from weekly content idea (email CTA)
export type WeeklyIdeaPrefill = {
  business_profile_id: number
  content_purpose: string
  topic: string
  keywords: string[]
  additional_context: string
  language: string
}

export const useWeeklyIdeaPrefill = (ideaId: string | null, options = {}) => {
  return useQuery({
    queryKey: [...contentQueryKeys.all, 'weekly-idea-prefill', ideaId] as const,
    queryFn: async () => {
      const response = await api.get<WeeklyIdeaPrefill>(`/content/weekly-idea/${ideaId}/prefill`)
      return response.data
    },
    enabled: !!ideaId && Number(ideaId) > 0,
    staleTime: 5 * 60 * 1000,
    ...options,
  })
}

// Latest weekly idea for dashboard (show "Generate content with idea" banner)
export type LatestWeeklyIdea = {
  id: number
  business_profile_id: number
  dashboard_teaser: string
  idea_summary: string
}

export const useLatestWeeklyIdea = (options = {}) => {
  return useQuery({
    queryKey: [...contentQueryKeys.all, 'weekly-ideas-latest'] as const,
    queryFn: async () => {
      const response = await api.get<{ idea: LatestWeeklyIdea | null }>('/content/weekly-ideas/latest')
      return response.data
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  })
}

// Alias for backward compatibility (deprecated - use useArchiveContentGeneration instead)
export const useDeleteContentGeneration = useArchiveContentGeneration

// Note: Favorite and saved content endpoints not available in current API
// export const useToggleFavorite = () => { ... }
// export const useSavedContent = () => { ... }
